import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { History, Calculator, LockKeyhole } from "lucide-react";
import api from "@/lib/api";
import { useReference } from "@/context/ReferenceContext";
import { useRestrictedVault } from "@/context/RestrictedVaultContext";
import { fmtCurrency, fmtDate } from "@/lib/format";

const NONE = "__none__";

function computeCommission(form) {
  const monthly = Number(form.monthly_premium || form.quoted_premium || form.original_premium || form.revised_premium || 0);
  const pct = Number(form.commission_percentage || 0);
  const annual = monthly * 12;
  const expected = annual * pct / 100;
  const received = Number(form.commission_received || 0);
  const leadCost = Number(form.lead_cost || 0);
  const net = received - leadCost;
  const chargeback = Number(form.chargeback_amount || 0);
  const netAfter = net - chargeback;
  return { monthly, pct, annual, expected, received, leadCost, net, chargeback, netAfter };
}

function CommissionCalc({ form }) {
  const c = computeCommission(form);
  const rows = [
    ["Annual Premium", fmtCurrency(c.annual)],
    ["Commission %", `${c.pct}%`],
    ["Expected Commission", fmtCurrency(c.expected)],
    ["Actual Commission Received", fmtCurrency(c.received)],
    ["Lead Cost", fmtCurrency(c.leadCost)],
    ["Net Commission", fmtCurrency(c.net)],
    ["Chargeback Amount", fmtCurrency(c.chargeback)],
    ["Net After Chargeback", fmtCurrency(c.netAfter)],
  ];
  return (
    <div className="sm:col-span-2 rounded-md border border-border bg-secondary/50 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Calculator className="h-4 w-4 text-primary" /> Commission Summary <span className="text-muted-foreground font-normal">(auto-calculated)</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {rows.map(([label, val]) => (
          <div key={label} className="rounded bg-card px-2.5 py-1.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`text-sm font-semibold tabular-nums ${label.startsWith("Net") && (label.includes("After") ? c.netAfter : c.net) < 0 ? "text-destructive" : ""}`}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityTimeline({ entityType, entityId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!entityType || !entityId) return;
    api.get(`/activity/${entityType}/${entityId}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [entityType, entityId]);
  if (!entityType || !entityId) return null;
  return (
    <div className="sm:col-span-2 rounded-md border border-border p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
        <History className="h-4 w-4 text-primary" /> Activity History
      </div>
      {items === null ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground">No activity recorded yet.</div>
      ) : (
        <ol className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {items.map((a) => (
            <li key={a.id} className="border-l-2 border-primary/30 pl-3" data-testid="activity-item">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{a.action} · <span className="text-muted-foreground">{a.user}</span></span>
                <span className="text-[10px] text-muted-foreground">{fmtDate(a.timestamp)}</span>
              </div>
              {a.changes?.length > 0 && (
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {a.changes.slice(0, 6).map((ch) => (
                    <span key={ch.field} className="mr-2">
                      <span className="font-mono">{ch.field}</span>: {String(ch.from ?? "—")} → {String(ch.to ?? "—")}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function RecordDialog({ open, onOpenChange, title, fields, initial, onSave, saving, calc, entityType }) {
  const [form, setForm] = useState({});
  const { opts } = useReference();
  const { openVault } = useRestrictedVault();

  useEffect(() => {
    if (open) setForm(initial || {});
  }, [open, initial]);

  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const editableFields = useMemo(() => fields.filter((f) => !f.readOnly), [fields]);
  const fieldKeys = useMemo(() => new Set(editableFields.map((f) => f.key)), [editableFields]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let payload = Object.fromEntries(editableFields.map((field) => [field.key, form[field.key]]));
    if (calc) {
      const c = computeCommission(form);
      if (c.monthly > 0) {
        payload.annual_premium = Math.round(c.annual * 100) / 100;
        if (fieldKeys.has("expected_commission") && c.pct > 0) payload.expected_commission = Math.round(c.expected * 100) / 100;
      }
      if (fieldKeys.has("net_commission") && (c.received || c.leadCost || c.chargeback)) {
        payload.net_commission = Math.round(c.netAfter * 100) / 100;
      }
    }
    onSave(payload);
  };

  const openRestricted = () => {
    if (!initial?.id) return;
    onOpenChange(false);
    openVault({ clientId: initial.id, tab: "vault" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>
            {entityType === "leads"
              ? "Standard lead information is shown here. Restricted client data is stored separately and remains masked by default."
              : "Review and update this record."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => {
            const val = form[f.key];
            if (f.readOnly) {
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs">{f.label}</Label>
                  <Input data-testid={`field-${f.key}`} value={val ?? ""} readOnly disabled />
                </div>
              );
            }
            if (f.type === "textarea") {
              return (
                <div key={f.key} className="sm:col-span-2 space-y-1.5">
                  <Label className="text-xs">{f.label}</Label>
                  <Textarea data-testid={`field-${f.key}`} value={val || ""} onChange={(e) => setVal(f.key, e.target.value)} />
                </div>
              );
            }
            if (f.type === "switch") {
              return (
                <div key={f.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <Label className="text-xs">{f.label}</Label>
                  <Switch data-testid={`field-${f.key}`} checked={!!val} onCheckedChange={(c) => setVal(f.key, c)} />
                </div>
              );
            }
            if (f.type === "select") {
              const baseOpts = f.optionsKey ? (opts[f.optionsKey] || []) : (f.options || []);
              const options = !f.required && !baseOpts.includes("") ? ["", ...baseOpts] : baseOpts;
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs">{f.label}</Label>
                  <Select value={val ? String(val) : ""} onValueChange={(v) => setVal(f.key, v === NONE ? "" : v)}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {options.map((o) => (
                        <SelectItem key={o || NONE} value={o === "" ? NONE : o}>{o === "" ? "— None —" : o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            return (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-xs">{f.label}</Label>
                <Input
                  data-testid={`field-${f.key}`}
                  type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  value={val ?? ""}
                  required={f.required}
                  onChange={(e) => setVal(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                />
              </div>
            );
          })}

          {calc && <CommissionCalc form={form} />}
          {entityType && initial?.id && <ActivityTimeline entityType={entityType} entityId={initial.id} />}

          <DialogFooter className="sm:col-span-2 mt-2 flex-wrap">
            {entityType === "leads" && initial?.id && (
              <Button type="button" variant="outline" onClick={openRestricted} data-testid="restricted-client-data-btn">
                <LockKeyhole className="mr-2 h-4 w-4" /> Restricted Client Data
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="dialog-save-btn">{saving ? "Saving…" : "Save Record"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
